import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  TvIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useMyChannels } from '@/hooks/useChannels';
import NotificationBell from '../notifications/NotificationBell';
import ChannelSwitcher from '../channel/ChannelSwitcher';

function derivePageTitle(pathname: string): string {
  if (pathname === '/') return 'Home';
  if (pathname === '/templates') return 'Templates';
  if (pathname === '/templates/create') return 'Coaching Studio';
  if (pathname === '/evaluate') return 'Evaluate';
  if (pathname === '/evaluations') return 'Results';

  if (/^\/templates\/[^/]+\/edit$/.test(pathname)) return 'Edit Template';
  if (/^\/templates\/[^/]+$/.test(pathname)) return 'Template Details';
  if (/^\/evaluate\/[^/]+$/.test(pathname)) return 'Evaluate';
  if (/^\/evaluations\/[^/]+$/.test(pathname)) return 'Evaluation Results';

  if (pathname === '/channels/create') return 'My Channel';
  if (/^\/channels\/[^/]+\/manage$/.test(pathname)) return 'Manage Channel';
  if (/^\/channels\/[^/]+$/.test(pathname)) return 'Channel';

  if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'Admin Panel';

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Home';
  const last = segments[segments.length - 1];
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
}

function TopBar() {
  const { pathname } = useLocation();
  const title = derivePageTitle(pathname);
  // Dark-only (owner 2026-08-01): the light/dark toggle was removed with the
  // prototype design-language adoption. See ThemeContext.tsx for the rationale.
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = user?.display_name || user?.email || 'User';
  const email = user?.email || '';
  const initials = displayName.charAt(0).toUpperCase();
  const isCoach = user?.role !== 'athlete';
  const roleLabel = isCoach ? 'Coach' : 'Athlete';

  // Resolve "Your channel" destination:
  // - If the coach owns at least one channel, link to its manage page.
  // - Otherwise (new coaches or loading), fall back to /channels/create.
  const { data: myChannels, isLoading: channelsLoading } = useMyChannels();
  const ownedChannel = !channelsLoading && myChannels
    ? myChannels.find((ch) => ch.owner_id === user?.id) ?? null
    : null;
  const channelLinkTo = ownedChannel
    ? `/channels/${ownedChannel.slug}/manage`
    : '/channels/create';

  // Role accent colors
  // PT-3: badge bg/border derive from the SAME token as the text so they track
  // the theme (the old rgba literals were fossils — incl. the pre-blue GREEN).
  const roleColor = isCoach ? 'var(--color-accent-blue)' : 'var(--color-accent)';
  const roleBg = `color-mix(in srgb, ${isCoach ? 'var(--color-accent-blue)' : 'var(--color-accent)'} 12%, transparent)`;
  const roleBorder = `color-mix(in srgb, ${isCoach ? 'var(--color-accent-blue)' : 'var(--color-accent)'} 30%, transparent)`;

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);

  return (
    <>
      <style>{`
        @keyframes sf-dropdown-in {
          from { opacity: 0; transform: scale(0.95) translateY(-6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .sf-dropdown {
          animation: sf-dropdown-in 0.18s cubic-bezier(0.22, 1, 0.36, 1) both;
          transform-origin: top right;
        }
        .sf-avatar-btn:hover .sf-avatar-ring {
          box-shadow: 0 0 0 2px var(--color-dark-base), 0 0 0 4px var(--color-accent), 0 0 14px color-mix(in srgb, var(--color-accent) 30%, transparent);
        }
        .sf-menu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 9px 12px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: var(--color-text-secondary);
          font-size: 13px;
          font-weight: 500;
          cursor: var(--cursor-hand);
          text-align: left;
          transition: background-color 130ms ease, color 130ms ease;
        }
        .sf-menu-item:hover {
          background-color: var(--color-dark-elevated-2);
          color: var(--color-text-primary);
        }
        .sf-menu-item.danger:hover {
          background-color: rgba(240, 93, 94, 0.08);
          color: var(--color-danger);
        }
        .sf-menu-item svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          opacity: 0.7;
        }
        .sf-menu-item:hover svg {
          opacity: 1;
        }
      `}</style>

      <header
        style={{
          height: '64px',
          backgroundColor: 'var(--color-dark-base)',
          borderBottom: '1px solid var(--color-border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        {/* Left: page title */}
        <div className="flex items-center gap-3">
          <div className="lg:hidden" style={{ width: '40px' }} />
          <h1
            style={{
              /* PT-3: editorial serif page title (Fraunces ruling); 'Sora' was
                 never loaded — it silently fell back to sans. */
              fontFamily: 'var(--font-editorial)',
              fontWeight: 400,
              fontSize: '18px',
              letterSpacing: '-0.01em',
              color: 'var(--color-text-primary)',
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>
        </div>

        {/* Right: channel switcher + notifications + profile (theme toggle
            removed — dark-only per owner 2026-08-01) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          {/* Channel switcher — shown when user has 2+ memberships or is an athlete */}
          <ChannelSwitcher />

          {/* Notification bell */}
          <NotificationBell />

          {/* Profile button + dropdown */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="Open user menu"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="sf-avatar-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                padding: '5px 12px 5px 5px',
                borderRadius: '999px',
                border: '1px solid var(--color-border-subtle)',
                backgroundColor: menuOpen
                  ? 'var(--color-dark-elevated-2)'
                  : 'var(--color-dark-elevated-1)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                transition: 'background-color 120ms ease, border-color 120ms ease',
                fontSize: '13px',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-strong)';
              }}
              onMouseLeave={(e) => {
                if (!menuOpen) {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-1)';
                }
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
              }}
            >
              {/* Avatar with animated ring on hover */}
              <div
                className="sf-avatar-ring"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '999px',
                  backgroundColor: 'var(--color-accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'box-shadow 180ms ease',
                }}
                aria-hidden="true"
              >
                <span
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize: '12px',
                    color: 'var(--color-text-inverse)',
                    lineHeight: 1,
                  }}
                >
                  {initials}
                </span>
              </div>

              {/* Name */}
              <span
                style={{
                  color: 'var(--color-text-primary)',
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </span>

              {/* Role badge — accent-colored */}
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: '999px',
                  backgroundColor: roleBg,
                  color: roleColor,
                  border: `1px solid ${roleBorder}`,
                  letterSpacing: '0.1px',
                }}
              >
                {roleLabel}
              </span>
            </button>

            {/* Dropdown menu */}
            {menuOpen && (
              <div
                role="menu"
                aria-label="User menu"
                className="sf-dropdown"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 10px)',
                  zIndex: 50,
                  width: '240px',
                  borderRadius: '14px',
                  border: '1px solid var(--color-border-subtle)',
                  backgroundColor: 'var(--color-dark-elevated-1)',
                  padding: '8px',
                  boxShadow: 'var(--shadow-modal)',
                }}
              >
                {/* User identity block */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px 14px',
                  }}
                >
                  {/* Large avatar */}
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '999px',
                      backgroundColor: 'var(--color-accent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 0 10px color-mix(in srgb, var(--color-accent) 20%, transparent)',
                    }}
                    aria-hidden="true"
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontWeight: 700,
                        fontSize: '15px',
                        color: 'var(--color-text-inverse)',
                        lineHeight: 1,
                      }}
                    >
                      {initials}
                    </span>
                  </div>

                  {/* Name + email */}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontWeight: 600,
                        fontSize: '13px',
                        color: 'var(--color-text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {displayName}
                    </div>
                    {email && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: 'var(--color-text-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginTop: '2px',
                        }}
                      >
                        {email}
                      </div>
                    )}
                    {/* Role badge in dropdown */}
                    <span
                      style={{
                        display: 'inline-block',
                        marginTop: '5px',
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 7px',
                        borderRadius: '999px',
                        backgroundColor: roleBg,
                        color: roleColor,
                        border: `1px solid ${roleBorder}`,
                        letterSpacing: '0.2px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {roleLabel}
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: '1px',
                    backgroundColor: 'var(--color-border-subtle)',
                    margin: '0 4px 6px',
                  }}
                />

                {/* Menu items */}
                <Link
                  to="/profile"
                  role="menuitem"
                  className="sf-menu-item"
                  onClick={() => setMenuOpen(false)}
                  style={{ textDecoration: 'none' }}
                >
                  <UserCircleIcon />
                  Your profile
                </Link>

                {isCoach && (
                  <Link
                    to={channelLinkTo}
                    role="menuitem"
                    className="sf-menu-item"
                    onClick={() => setMenuOpen(false)}
                    style={{ textDecoration: 'none' }}
                    data-testid="your-channel-link"
                  >
                    <TvIcon />
                    Your channel
                  </Link>
                )}

                <Link
                  to="/settings"
                  role="menuitem"
                  className="sf-menu-item"
                  onClick={() => setMenuOpen(false)}
                  style={{ textDecoration: 'none' }}
                >
                  <Cog6ToothIcon />
                  Settings
                </Link>

                {/* Divider before destructive action */}
                <div
                  style={{
                    height: '1px',
                    backgroundColor: 'var(--color-border-subtle)',
                    margin: '6px 4px',
                  }}
                />

                <button
                  type="button"
                  role="menuitem"
                  className="sf-menu-item danger"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                >
                  <ArrowRightOnRectangleIcon />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

export default TopBar;
